import React from "react";

/**
 * UserTierBadge — shows the user's effective tier + the SOURCE of that tier
 * (paid sub vs feedback reward vs lifetime). Drives the Tier 1 expiration nudge
 * and the Tier 2 "locked in for life" flex.
 *
 * Expects `userTierData` from GET /api/subscription/feature-access:
 *   { tier, tier_source, elite_credit_until, lifetime_elite, ... }
 */
export default function UserTierBadge({ userTierData }) {
  if (!userTierData) return null;
  const { tier, tier_source, elite_credit_until } = userTierData;

  const formatExpiryDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (tier === "free") {
    return (
      <div
        className="flex items-center gap-2 p-3 bg-neutral-950 border border-neutral-800 rounded-lg"
        data-testid="user-tier-badge-free"
      >
        <span className="text-xs font-bold tracking-wider text-neutral-400 uppercase">
          Free Tier
        </span>
      </div>
    );
  }

  return (
    <div
      className="p-3 bg-neutral-950 border border-neutral-800 rounded-xl space-y-1 font-sans"
      data-testid="user-tier-badge"
    >
      <div className="flex items-center justify-between">
        {/* Tier Name */}
        <span className="text-sm font-black tracking-black text-white uppercase flex items-center gap-1.5">
          <span className="text-amber-500">⚡</span> {tier} Tier
        </span>

        {/* Contextual Badge Icon/Text */}
        {tier_source === "lifetime_elite_reward" && (
          <span
            className="text-[10px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
            data-testid="tier-source-founder"
          >
            🏆 Founder
          </span>
        )}
        {tier_source === "time_bounded_credit" && (
          <span
            className="text-[10px] bg-blue-500/10 text-blue-500 border border-blue-500/20 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
            data-testid="tier-source-tester"
          >
            🧪 Tester
          </span>
        )}
        {tier_source === "stripe_subscription" && (
          <span
            className="text-[10px] bg-green-500/10 text-green-500 border border-green-500/20 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
            data-testid="tier-source-active"
          >
            Active
          </span>
        )}
      </div>

      {/* Dynamic Subtext based on the Source */}
      <p className="text-xs text-neutral-500 font-medium">
        {tier_source === "lifetime_elite_reward" && (
          "Earned via beta feedback. You're locked in for life."
        )}
        {tier_source === "time_bounded_credit" && (
          `Feedback reward — expires ${formatExpiryDate(elite_credit_until)}`
        )}
        {tier_source === "stripe_subscription" && "Subscribed via Stripe."}
      </p>
    </div>
  );
}
